import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/24/outline';
import { elearningAPI } from '../../../services/elearningApi';
import { Course, CourseDifficulty, CourseStatus } from '../../../types/elearning';
import Button from '../../../components/ui/Button';
import Card from '../../../components/ui/Card';
import Badge from '../../../components/ui/Badge';
import LoadingSkeleton from '../../../components/ui/LoadingSkeleton';
import ErrorState from '../../../components/ui/ErrorState';
import EmptyState from '../../../components/ui/EmptyState';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { usePageTitle } from '../../../hooks/usePageTitle';

const statusLabels: Record<CourseStatus, string> = {
  [CourseStatus.DRAFT]: 'Draft',
  [CourseStatus.PUBLISHED]: 'Published',
  [CourseStatus.ARCHIVED]: 'Archived',
};

const difficultyLabels: Record<CourseDifficulty, string> = {
  [CourseDifficulty.BEGINNER]: 'Beginner',
  [CourseDifficulty.INTERMEDIATE]: 'Intermediate',
  [CourseDifficulty.ADVANCED]: 'Advanced',
};

export default function CourseList() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  usePageTitle('eLearning courses');

  const loadCourses = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await elearningAPI.fetchCourses();
      setCourses(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load courses'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadCourses();
  }, []);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <LoadingSkeleton lines={8} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ErrorState error={error} title="Failed to load courses" onRetry={loadCourses} />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Breadcrumb
        items={[
          { label: 'Home', to: '/' },
          { label: 'eLearning', to: '/elearning' },
          { label: 'Courses' },
        ]}
      />

      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">eLearning courses</h1>
          <p className="mt-1 text-sm text-gray-600">
            Every course, including drafts
          </p>
        </div>
        <Button
          onClick={() => navigate('/elearning/admin/courses/new')}
          className="flex items-center gap-2"
        >
          <PlusIcon className="h-5 w-5" />
          Create Course
        </Button>
      </div>

      {courses.length === 0 ? (
        <EmptyState
          title="No courses yet"
          description="Create a course to start building modules."
          actionLabel="Create Course"
          onAction={() => navigate('/elearning/admin/courses/new')}
        />
      ) : (
        <div className="space-y-4">
          {courses.map((course) => {
            const moduleCount = course.modules?.length ?? 0;
            return (
              <Link
                key={course.id}
                to={`/elearning/admin/courses/${course.id}/edit`}
                className="block focus-ring rounded-lg"
              >
                <Card hover>
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">{course.title}</h2>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge>{statusLabels[course.status] || course.status}</Badge>
                    <Badge>{difficultyLabels[course.difficulty] || course.difficulty}</Badge>
                    <span className="text-sm text-gray-600">
                      {moduleCount} {moduleCount === 1 ? 'module' : 'modules'}
                    </span>
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
